import type { ScrollAnchor } from "../hooks/usePageVisibilityObserver";

type PageTextIndex = Record<number, string>;

const MAX_CHARS = 4000;

export type VisibleContext = {
  kind: "visible";
  text: string;
  pageNumbers: number[];
};

/**
 * Expands context around the user's scroll position.
 * Uses scroll direction awareness to prioritize relevant pages.
 */
export function expandScrollContext(
  anchor: ScrollAnchor,
  pageTextIndex: PageTextIndex
): VisibleContext {
  const pages: number[] = [];
  const texts: string[] = [];

  const { pageNumber, pageRatio } = anchor;

  // Determine scroll direction from offset ratio
  // < 0.33 = just entered page (likely scrolling down into it)
  // 0.33-0.66 = reading center
  // > 0.66 = near bottom (scrolling down)
  const direction =
    pageRatio > 0.6 ? "down" :
    pageRatio < 0.4 ? "up" :
    "neutral";

  // Prioritize pages based on scroll direction
  const candidates =
    direction === "down"
      ? [pageNumber, pageNumber + 1, pageNumber + 2, pageNumber - 1]
      : direction === "up"
      ? [pageNumber, pageNumber - 1, pageNumber - 2, pageNumber + 1]
      : [pageNumber - 1, pageNumber, pageNumber + 1];

  let charCount = 0;

  for (const p of candidates) {
    if (p < 1) continue; // Skip invalid page numbers
    
    const text = pageTextIndex[p];
    if (!text) continue;

    pages.push(p);
    texts.push(`[Page ${p}]\n${text}`);
    charCount += text.length;

    if (charCount > MAX_CHARS) break;
  }

  // Sort pages for consistent ordering in context
  pages.sort((a, b) => a - b);

  return {
    kind: "visible",
    pageNumbers: pages,
    text: texts.join("\n\n"),
  };
}
